// src/components/landing/HeroSection.jsx - v1.1
import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Stack } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import img_hero_bg_1 from '../../assets/images/landing/hero-bg-1.jpg';
import img_hero_bg_2 from '../../assets/images/landing/hero-bg-2.jpg';
import img_hero_bg_3 from '../../assets/images/landing/hero-bg-3.jpg';
import img_hero_indian from '../../assets/images/landing/hero-indian.jpg';
const BG_IMAGES = [
  img_hero_bg_1,
  img_hero_bg_2,
  img_hero_bg_3,
];
const heroIndian = img_hero_indian;

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease: 'easeOut' },
});

export default function HeroSection({ onEnroll }) {
  const [bgIndex, setBgIndex] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setBgIndex((p) => (p + 1) % BG_IMAGES.length), 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        px: { xs: 3, md: 8 },
        pt: { xs: 10, md: 14 },
        pb: { xs: 8, md: 10 },
        gap: { xs: 0, md: 8 },
        flexDirection: { xs: 'column', md: 'row' },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* === Background carousel === */}
      <Box sx={{ position: 'absolute', inset: 0, zIndex: 0 }}>
        <AnimatePresence>
          <motion.div
            key={bgIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.4, ease: 'easeInOut' }}
            style={{ position: 'absolute', inset: 0 }}
          >
            <Box
              component="img"
              src={BG_IMAGES[bgIndex]}
              alt=""
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </motion.div>
        </AnimatePresence>
        {/* Dark gradient overlay */}
        <Box sx={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(155deg,rgba(6,13,31,0.88) 0%,rgba(15,52,96,0.80) 40%,rgba(14,106,135,0.70) 75%,rgba(14,165,197,0.60) 100%)',
        }} />
        {/* Dot-indicator dots */}
        <Box sx={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 1, zIndex: 2 }}>
          {BG_IMAGES.map((_, i) => (
            <Box
              key={i}
              onClick={() => setBgIndex(i)}
              sx={{
                width: i === bgIndex ? 24 : 8, height: 8, borderRadius: 4, cursor: 'pointer',
                bgcolor: i === bgIndex ? '#38bdf8' : 'rgba(255,255,255,0.35)',
                transition: 'all 0.4s',
              }}
            />
          ))}
        </Box>
      </Box>

      {/* === Left: text === */}
      <Box sx={{ flex: 1, maxWidth: 580, position: 'relative', zIndex: 2, mb: { xs: 6, md: 0 } }}>
        <motion.div {...fadeUp(0.1)}>
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 1,
            background: 'rgba(255,255,255,0.10)', backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.18)',
            px: 2, py: 0.75, borderRadius: 10, fontSize: '0.78rem', color: '#7dd3fc', mb: 3,
          }}>
            ✦ &nbsp; Govt. Approved · Trusted Education Partner
          </Box>
        </motion.div>

        <motion.div {...fadeUp(0.25)}>
          <Typography sx={{
            fontSize: { xs: '2.2rem', md: '3.6rem' },
            fontWeight: 900, lineHeight: 1.15, letterSpacing: '-1px', mb: 2.5, color: '#fff',
          }}>
            Launch Your Career<br />
            with{' '}
            <Box component="span" sx={{
              background: 'linear-gradient(90deg,#38bdf8,#22d3ee,#34d399)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
            }}>
              Quality IT Education
            </Box>
          </Typography>
        </motion.div>

        <motion.div {...fadeUp(0.4)}>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '1rem', lineHeight: 1.75, maxWidth: 460, mb: 4.5 }}>
            BSS IT Mission — the IT Division of Bharat Sevak Samaj — brings computer education to every corner of Kerala.
          </Typography>
        </motion.div>

        <motion.div {...fadeUp(0.55)}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 6 }}>
            <Button href="#courses" variant="contained" size="large" sx={{
              background: 'linear-gradient(135deg,#1d6cf0,#0ea5e9)', borderRadius: 3, px: 4, py: 1.75,
              fontWeight: 700, fontSize: '0.95rem', textTransform: 'none',
              boxShadow: '0 8px 24px rgba(29,108,240,0.45)',
              '&:hover': { boxShadow: '0 12px 32px rgba(29,108,240,0.6)', transform: 'translateY(-2px)' },
              transition: 'all 0.2s',
            }}>
              Explore All Courses →
            </Button>
            <Button href="#contact" variant="outlined" size="large" sx={{
              color: '#fff', borderColor: 'rgba(255,255,255,0.3)', borderRadius: 3, px: 4, py: 1.75,
              fontWeight: 600, fontSize: '0.95rem', textTransform: 'none',
              backdropFilter: 'blur(8px)', background: 'rgba(255,255,255,0.08)',
              '&:hover': { borderColor: 'rgba(255,255,255,0.55)', background: 'rgba(255,255,255,0.13)' },
            }}>
              Get in Touch
            </Button>
          </Stack>
        </motion.div>

        <motion.div {...fadeUp(0.7)}>
          <Stack direction="row" spacing={5}>
            {[{ n: '150+', l: 'Training Centers' }, { n: '300+', l: 'Expert Faculties' }, { n: '5000+', l: 'Students Trained' }].map((s) => (
              <Box key={s.l}>
                <Typography sx={{ fontSize: '1.9rem', fontWeight: 900, color: '#38bdf8', lineHeight: 1 }}>{s.n}</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', mt: 0.5 }}>{s.l}</Typography>
              </Box>
            ))}
          </Stack>
        </motion.div>
      </Box>

      {/* === Right: featured Indian institution image === */}
      <motion.div
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.9, delay: 0.3, ease: 'easeOut' }}
        style={{ flex: 1, display: 'flex', justifyContent: 'center', position: 'relative', zIndex: 2 }}
      >
        <Box sx={{ position: 'relative', width: { xs: '100%', md: 440 }, maxWidth: 440 }}>
          <Box sx={{
            width: '100%', height: { xs: 280, md: 420 }, borderRadius: 6, overflow: 'hidden',
            border: '2px solid rgba(255,255,255,0.15)',
            boxShadow: '0 40px 90px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06)',
          }}>
            <Box component="img" src={heroIndian} alt="BSS IT Mission Kerala students"
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </Box>

          {/* Float card: Districts */}
          <Box sx={{
            position: 'absolute', top: 24, right: { xs: 8, md: -24 },
            background: 'rgba(10,15,30,0.92)', backdropFilter: 'blur(16px)',
            border: '1px solid rgba(255,255,255,0.12)', borderRadius: 4,
            px: 2, py: 1.5, boxShadow: '0 16px 40px rgba(0,0,0,0.35)',
          }}>
            <Typography sx={{ fontSize: '1.5rem', fontWeight: 900, color: '#38bdf8', lineHeight: 1 }}>14</Typography>
            <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', mt: 0.5 }}>Districts<br/>Covered</Typography>
          </Box>
        </Box>
      </motion.div>
    </Box>
  );
}
